import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classnames from 'classnames';
import { isFuture } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { hideScrollbar } from '@sorare/core/src/style/utils';

import { Formation } from 'components/MatchView/Formation';

import { FootballFieldLines } from './FootballFieldLines';
import type {
  FootballField_game,
  FootballField_gameSubscription,
} from './__generated__/index.graphql';

const Root = styled.div`
  isolation: isolate;
  position: relative;
  color: var(--c-white);
  background-color: var(--c-nd-50);
  width: 100%;
  aspect-ratio: 600 / 1136;

  &.desktop {
    border-bottom-left-radius: var(--double-unit);
    border-bottom-right-radius: var(--double-unit);
    &:not(.noBorderRadius) {
      border-radius: var(--double-unit);
    }
    aspect-ratio: 1136 / 600;
  }
  ${hideScrollbar};
`;
const FootballFieldWrapper = styled.div`
  width: 100%;
`;
const FormationsContainer = styled(Vertical)`
  position: absolute;
  inset: 0;
  &.desktop {
    flex-direction: row;
    gap: 0;
    height: initial;
  }
  & > * {
    flex: 1;
  }
`;

type Props = {
  game: FootballField_game;
  highlightedPlayerSlugs?: string[];
  highlightInLineupPlayers?: boolean;
  onPlayerDetailsClick: (playerSlug: string, scoreId?: string) => void;
  desktop?: boolean;
};
export const FootballField = ({
  game,
  highlightedPlayerSlugs,
  highlightInLineupPlayers,
  onPlayerDetailsClick,
  desktop,
}: Props) => {
  const isLaptop = useIsDesktopAndAbove();
  if (!(game.homeTeam && game.awayTeam)) {
    return null;
  }
  const showCountdown =
    isFuture(game.date) &&
    !(
      game.homeFormation.startingLineupAvailable ||
      game.awayFormation.startingLineupAvailable
    );

  return (
    <Root className={classnames({ desktop, noBorderRadius: !isLaptop })}>
      <FootballFieldWrapper className={classnames({ desktop })}>
        <FootballFieldLines desktop={desktop} />
      </FootballFieldWrapper>
      <FormationsContainer className={classnames({ desktop })}>
        <Formation
          desktop={desktop}
          game={game}
          highlightedPlayerSlugs={highlightedPlayerSlugs}
          highlightInLineupPlayers={highlightInLineupPlayers}
          isHomeFormation
          onPlayerDetailsClick={onPlayerDetailsClick}
        />
        {!!showCountdown && (
          <Vertical center>
            <HeadlineL>
              <FormattedMessage
                id="MatchView.startIngLineups"
                defaultMessage="Starting Lineups in"
              />
            </HeadlineL>
            <TimeLeft time={game.date} Layout={HeadlineL} />
          </Vertical>
        )}
        <Formation
          desktop={desktop}
          game={game}
          highlightedPlayerSlugs={highlightedPlayerSlugs}
          highlightInLineupPlayers={highlightInLineupPlayers}
          onPlayerDetailsClick={onPlayerDetailsClick}
          reverse
        />
      </FormationsContainer>
    </Root>
  );
};

FootballField.fragments = {
  game: gql`
    fragment FootballField_game on Game {
      id
      date
      awayFormation {
        startingLineupAvailable
      }
      awayTeam {
        slug
        code
        pictureUrl(derivative: "avatar")
      }
      homeFormation {
        startingLineupAvailable
      }
      homeTeam {
        slug
        code
        pictureUrl(derivative: "avatar")
      }
      ...Formation_game
    }
    ${Formation.fragments.game}
  ` as TypedDocumentNode<FootballField_game>,
  subscriptions: {
    game: gql`
      fragment FootballField_gameSubscription on Game {
        id
        awayFormation {
          startingLineupAvailable
        }
        homeFormation {
          startingLineupAvailable
        }
        ...Formation_gameSubscription
      }
      ${Formation.fragments.subscriptions.game}
    ` as TypedDocumentNode<FootballField_gameSubscription>,
  },
};
